import { TeamDetailsResponseDto } from "@/lib/api/interfaces/teams.interface";
import {
  useTeamsApplyLogoEdit,
  useTeamsDelete,
  useTeamsDeleteLogo,
  useTeamsFindOneQuery,
  useTeamsPreviewBackgroundRemoval,
  useTeamsRestoreOriginalLogo,
  useTeamsUpdate,
  useTeamsUploadLogo,
} from "@/lib/api/requests/teams.requests";
import { toast } from "@/lib/toast";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";
import { Skeleton } from "@/ui-components/ui/skeleton";
import { zodResolver } from "@hookform/resolvers/zod";
import { Calendar, Edit2, Radio, UsersRound } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import z from "zod";
import {
  DetailsBaseDialog,
  DetailsBaseDialogMode,
  DialogContentItem,
  DialogContentSection,
  DialogHeaderSection,
} from "@/util-components/dialog/base-dialogs";
import { ManagedImageBusyAction, ManagedImageField, persistManagedImageDraft, ManagedImageFieldSkeleton } from "@/util-components/media";

const updateTeamSchema = z.object({
  title: z.string().min(1, { message: "Titel ist erforderlich" }),
  displayName: z.string().optional(),
  image: z.instanceof(File).optional(),
});

type UpdateTeamFormValues = z.infer<typeof updateTeamSchema>;

const TeamDetailsDialog = ({
  open,
  onClose,
  teamId,
  mode = "details",
}: {
  open: boolean;
  onClose: (refetchData: boolean) => void;
  teamId: string;
  mode?: DetailsBaseDialogMode;
}) => {
  const [isDeleteInProgress, setIsDeleteInProgress] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [canSave, setCanSave] = useState(false);
  const { data: team, isLoading } = useTeamsFindOneQuery(teamId, {
    enabled: open && !!teamId && !isDeleteInProgress,
  });
  const { mutateAsync: deleteTeam, isPending: isDeleting } = useTeamsDelete();

  if (!team && !isLoading) {
    return (
      <DetailsBaseDialog
        open={open}
        onClose={onClose}
        icon={UsersRound}
        title="Mannschaftsdetails"
        description="Informationen zur Mannschaft einsehen und bearbeiten."
        deleteTitle="Mannschaft löschen"
        deleteItemTitle=""
        onDeleteConfirm={() => {}}
        detailsContent={<div className="py-12 text-center text-muted-foreground">Mannschaft nicht gefunden.</div>}
        editContent={() => null}
      />
    );
  }

  const handleDelete = async () => {
    if (!teamId || isDeleteInProgress) return;

    setIsDeleteInProgress(true);

    try {
      await deleteTeam({ id: teamId });
      toast.success("Mannschaft wurde erfolgreich gelöscht");
      onClose(true);
    } catch (error) {
      toast.error("Mannschaft konnte nicht gelöscht werden", { error });
      throw error;
    } finally {
      setIsDeleteInProgress(false);
    }
  };

  return (
    <DetailsBaseDialog
      open={open}
      onClose={onClose}
      mode={mode}
      icon={UsersRound}
      title="Mannschaftsdetails"
      description="Informationen zur Mannschaft einsehen und bearbeiten."
      deleteTitle="Mannschaft löschen"
      deleteItemTitle={`${team?.title}`}
      deleteItemTypeValue={teamId}
      onDeleteConfirm={handleDelete}
      isDeleting={isDeleting || isDeleteInProgress}
      isSaving={isSaving}
      canSave={canSave}
      formId="edit-team-form"
      detailsContent={isLoading ? <TeamDetailsSkeleton /> : <TeamDetails team={team!} />}
      editContent={(onSaveSuccess) =>
        isLoading ? (
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <TeamDetailsSkeleton />
          </div>
        ) : (
          <TeamEditForm
            team={team!}
            onSaveSuccess={onSaveSuccess}
            setIsSaving={setIsSaving}
            setCanSave={setCanSave}
          />
        )
      }
    />
  );
};

const TeamDetailsSkeleton = () => {
  return (
    <div className="space-y-6 overflow-y-auto">
      <ManagedImageFieldSkeleton />
      <div className="grid grid-cols-2 gap-4">
        <Skeleton className="h-20 w-full rounded-xl" />
        <Skeleton className="h-20 w-full rounded-xl" />
      </div>
    </div>
  );
};

const TeamDetails = ({ team }: { team: TeamDetailsResponseDto }) => {
  const activeLogoUrl = team.logoState?.active?.url || team.logoUrl;

  return (
    <>
      <DialogHeaderSection>
        <div className="flex flex-col items-center justify-center gap-4 text-center">
          <div className="relative flex h-24 w-24 items-center justify-center rounded-2xl bg-white p-3 shadow-md">
            {activeLogoUrl ? (
              <img src={activeLogoUrl} alt={team.title} className="h-full w-full object-contain" />
            ) : (
              <UsersRound className="h-10 w-10 text-gray-200" />
            )}
          </div>
          <div>
            <h2 className="text-2xl font-black tracking-tight text-white">{team.title}</h2>
            {team.displayName ? <p className="mt-1 text-sm font-medium text-white/80">{team.displayName}</p> : null}
          </div>
        </div>
      </DialogHeaderSection>

      <DialogContentSection title="Informationen" icon={Radio}>
        <div className="grid gap-3 sm:grid-cols-2">
          <DialogContentItem label="Erstellt am" value={new Date(team.createdAt).toLocaleDateString("de-DE")} icon={Calendar} />
          <DialogContentItem
            label="Zuletzt bearbeitet"
            value={new Date(team.updatedAt).toLocaleDateString("de-DE")}
            icon={Edit2}
          />
        </div>
      </DialogContentSection>
    </>
  );
};

const TeamEditForm = ({
  onSaveSuccess,
  team,
  setIsSaving,
  setCanSave,
}: {
  onSaveSuccess: () => void;
  team: TeamDetailsResponseDto;
  setIsSaving: (val: boolean) => void;
  setCanSave: (val: boolean) => void;
}) => {
  const { mutateAsync: updateTeam } = useTeamsUpdate();
  const { mutateAsync: uploadLogo } = useTeamsUploadLogo();
  const { mutateAsync: deleteLogo } = useTeamsDeleteLogo();
  const { mutateAsync: applyLogoEdit } = useTeamsApplyLogoEdit();
  const { mutateAsync: restoreOriginalLogo } = useTeamsRestoreOriginalLogo();
  const { mutateAsync: previewBackgroundRemoval } = useTeamsPreviewBackgroundRemoval();
  const [mediaBusyAction, setMediaBusyAction] = useState<ManagedImageBusyAction>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<UpdateTeamFormValues>({
    resolver: zodResolver(updateTeamSchema),
    mode: "onChange",
    defaultValues: {
      title: team.title,
      displayName: team.displayName || "",
      image: undefined,
    },
  });

  const watchedValues = form.watch();
  const selectedImageFile = watchedValues.image;

  useEffect(() => {
    setIsSaving(isSubmitting || mediaBusyAction !== null);
  }, [isSubmitting, mediaBusyAction, setIsSaving]);

  const hasChanges = useMemo(
    () =>
      watchedValues.title !== team.title ||
      (watchedValues.displayName ?? "") !== (team.displayName ?? "") ||
      !!watchedValues.image,
    [team.displayName, team.title, watchedValues.displayName, watchedValues.image, watchedValues.title]
  );

  useEffect(() => {
    setCanSave(form.formState.isValid && hasChanges && mediaBusyAction === null);
  }, [form.formState.isValid, hasChanges, mediaBusyAction, setCanSave]);

  const runMediaAction = async ({
    action,
    task,
    successMessage,
    errorMessage,
  }: {
    action: Exclude<ManagedImageBusyAction, null>;
    task: () => Promise<unknown>;
    successMessage: string;
    errorMessage: string;
  }) => {
    setMediaBusyAction(action);

    try {
      await task();
      toast.success(successMessage);
    } catch (error) {
      toast.error(errorMessage, { error });
    } finally {
      setMediaBusyAction(null);
    }
  };

  const onSubmit = async (data: UpdateTeamFormValues) => {
    setIsSubmitting(true);

    try {
      await updateTeam({
        id: team._id,
        data: {
          title: data.title,
          displayName: data.displayName || undefined,
        },
      });

      await persistManagedImageDraft({
        entityId: team._id,
        file: data.image,
        uploadImage: uploadLogo,
        applyImageEdit: applyLogoEdit,
      });

      toast.success("Mannschaft wurde erfolgreich aktualisiert");
      onSaveSuccess();
    } catch (error) {
      toast.error("Mannschaft konnte nicht gespeichert werden", { error });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form id="edit-team-form" onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex-1 space-y-6 overflow-y-auto p-6">
          <FormField
            control={form.control}
            name="image"
            render={() => (
              <FormItem>
                <FormControl>
                  <ManagedImageField
                    value={team.logoState}
                    file={selectedImageFile}
                    onFileChange={(nextFile) => form.setValue("image", nextFile ?? undefined, { shouldDirty: true, shouldValidate: true })}
                    onDeleteRemote={() =>
                      runMediaAction({
                        action: "delete",
                        task: () => deleteLogo({ id: team._id }),
                        successMessage: "Wappen wurde entfernt",
                        errorMessage: "Wappen konnte nicht entfernt werden",
                      })
                    }
                    onApplyEdit={(edit) =>
                      runMediaAction({
                        action: "applyEdit",
                        task: () => applyLogoEdit({ id: team._id, data: edit }),
                        successMessage: "Bildbearbeitung wurde übernommen",
                        errorMessage: "Wappen konnte nicht bearbeitet werden",
                      })
                    }
                    onRestoreOriginal={() =>
                      runMediaAction({
                        action: "restoreOriginal",
                        task: () => restoreOriginalLogo({ id: team._id }),
                        successMessage: "Originalwappen wurde wiederhergestellt",
                        errorMessage: "Originalwappen konnte nicht wiederhergestellt werden",
                      })
                    }
                    disabled={isSubmitting}
                    busyAction={isSubmitting ? "saving" : mediaBusyAction}
                    emptyTitle="Wappen auswählen"
                    previewBackgroundRemoval={async (file) => previewBackgroundRemoval({ file })}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel className="text-sm font-medium text-gray-700">
                    Mannschaftsname <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="C Jugend"
                      {...field}
                      className="h-11 border-gray-200 transition-all focus:border-blue-500 focus:ring-blue-500/20"
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="displayName"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel className="text-sm font-medium text-gray-700">Anzeigename</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="FC Musterhausen III"
                      {...field}
                      className="h-11 border-gray-200 transition-all focus:border-blue-500 focus:ring-blue-500/20"
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      </form>
    </Form>
  );
};

export default TeamDetailsDialog;
